import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { toast } from "./ui/sonner";
import { Users, X, Loader2, Bell, BellOff } from "lucide-react";

const MGR_ROLES = ["admin", "ceo", "sales_vp", "sales_manager"];

export default function TeamReminderOverview() {
  const { lang, user } = useApp();
  const zh = lang === "zh";
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!MGR_ROLES.includes(user.role)) return null;

  const load = async () => {
    setOpen(true);
    setLoading(true);
    try { const { data } = await api.get("/crm/team-reminder-overview"); setRows(data.rows || []); }
    catch { setRows([]); toast.error(zh ? "加载失败" : "Load failed"); }
    setLoading(false);
  };

  return (
    <>
      <button data-testid="team-reminder-overview-btn" onClick={load}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors">
        <Users className="h-4 w-4" />{zh ? "团队提醒总览" : "Team overview"}
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="team-reminder-overview-modal" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-800">{zh ? "全团队提醒偏好与待处理" : "Team reminder overview"}</h3>
              <button data-testid="team-overview-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-red-600"><X className="h-5 w-5" /></button>
            </div>
            <div className="overflow-auto p-4">
              {loading ? <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-slate-400" /></div>
                : !rows || rows.length === 0 ? <p className="text-center text-slate-400 text-sm py-8" data-testid="team-overview-empty">{zh ? "暂无销售账号" : "No sales accounts"}</p>
                : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
                        <th className="py-2 pr-2 font-medium">{zh ? "销售" : "Sales"}</th>
                        <th className="py-2 px-2 font-medium">{zh ? "低分提醒" : "Low-score"}</th>
                        <th className="py-2 px-2 font-medium">{zh ? "阈值" : "Threshold"}</th>
                        <th className="py-2 px-2 font-medium">{zh ? "无推进天数" : "Stale days"}</th>
                        <th className="py-2 px-2 font-medium">{zh ? "提醒时段" : "Hour"}</th>
                        <th className="py-2 px-2 font-medium text-red-600">{zh ? "待处理低分" : "Low unread"}</th>
                        <th className="py-2 px-2 font-medium text-amber-600">{zh ? "待处理降温" : "Cooling unread"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.username} data-testid={`team-overview-row-${r.username}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="py-2 pr-2">
                            <span className="font-medium text-slate-800">{r.name}</span>
                            {r.region && <span className="ml-1.5 text-[10px] text-slate-400">{r.region}</span>}
                          </td>
                          <td className="py-2 px-2">
                            {r.prefs.lowFollowupEnabled
                              ? <span className="inline-flex items-center gap-1 text-[11px] text-green-700"><Bell className="h-3 w-3" />{zh ? "开" : "On"}</span>
                              : <span className="inline-flex items-center gap-1 text-[11px] text-slate-400"><BellOff className="h-3 w-3" />{zh ? "关" : "Off"}</span>}
                          </td>
                          <td className="py-2 px-2 text-slate-600">{r.prefs.lowScoreThreshold}</td>
                          <td className="py-2 px-2 text-slate-600">{r.prefs.staleDays}</td>
                          <td className="py-2 px-2 text-slate-600">{String(r.prefs.reminderHour).padStart(2, "0")}:00</td>
                          <td className="py-2 px-2">
                            <span data-testid={`team-overview-low-${r.username}`} className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${r.lowUnread > 0 ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-400"}`}>{r.lowUnread}</span>
                          </td>
                          <td className="py-2 px-2">
                            <span data-testid={`team-overview-cooling-${r.username}`} className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${r.coolingUnread > 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-400"}`}>{r.coolingUnread}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
